import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '@/mocks/articles';
import { SITE_URL, SITE_NAME, SITE_LOGO } from '@/lib/seo';

function injectJsonLd(data: object) {
  document.querySelectorAll('script[data-readdy-schema]').forEach((s) => s.remove());

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-readdy-schema', 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

export default function SchemaOrg() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const path = location.pathname;
    let data: object;

    if (path === '/') {
      const faqItems = [1, 2, 3, 4, 5].map((n) => ({
        '@type': 'Question',
        name: t(`faq_q${n}`),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t(`faq_a${n}`),
        },
      }));

      data = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            name: SITE_NAME,
            url: SITE_URL,
            logo: SITE_LOGO,
          },
          {
            '@type': 'WebSite',
            name: SITE_NAME,
            url: SITE_URL,
            inLanguage: ['pt', 'en', 'de'],
            description: t('meta_home_desc'),
          },
          {
            '@type': 'FAQPage',
            mainEntity: faqItems,
          },
        ],
      };
    } else {
      const match = path.match(/^\/article\/([^/]+)/);
      const article = match ? articles.find((a) => a.id === match[1]) : undefined;

      if (match && article) {
        const url = `${SITE_URL}/article/${article.id}`;
        data = {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Article',
              headline: t(article.titleKey),
              description: t(article.descKey),
              image: [article.image],
              author: {
                '@type': 'Organization',
                name: SITE_NAME,
                url: SITE_URL,
              },
              publisher: {
                '@type': 'Organization',
                name: SITE_NAME,
                logo: {
                  '@type': 'ImageObject',
                  url: SITE_LOGO,
                },
              },
              datePublished: '2026-01-15',
              dateModified: '2026-08-26',
              mainEntityOfPage: url,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: SITE_URL,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: t(article.titleKey),
                  item: url,
                },
              ],
            },
          ],
        };
      } else {
        data = {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: SITE_NAME,
          url: SITE_URL,
        };
      }
    }

    injectJsonLd(data);
  }, [location.pathname, i18n.language, t]);

  return null;
}